import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, History as HistoryIcon, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { ListDTO } from '@translate-ai/shared';
import {
  LIST_DESCRIPTION_MAX_LENGTH,
  LIST_NAME_MAX_LENGTH,
} from '@translate-ai/shared';
import { useCreateList, useDeleteList, useLists, useUpdateList } from '@/api/hooks';
import { ApiError } from '@/api/client';
import { ErrorMessage } from '@/components/ErrorMessage';
import { LoadingPlaceholder } from '@/components/LoadingPlaceholder';

function localizeListName(list: ListDTO, fallback: (key: string) => string): string {
  if (list.isSystem && list.systemKey === 'favorites') return fallback('lists.favorites');
  return list.name;
}

export function Lists() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lists = useLists();
  const create = useCreateList();
  const [draft, setDraft] = useState<{ name: string; description: string } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (draft) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [draft]);

  const startNew = () => {
    setCreateError(null);
    setDraft({ name: '', description: '' });
  };

  const submitCreate = async () => {
    if (!draft) return;
    setCreateError(null);
    try {
      await create.mutateAsync({
        name: draft.name.trim(),
        description: draft.description.trim() || null,
      });
      setDraft(null);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('lists.title')}</h1>
        {!draft && (
          <button className="btn-primary h-8 px-3 text-sm" onClick={startNew}>
            <Plus className="h-4 w-4" />
            {t('lists.newList')}
          </button>
        )}
      </div>

      {lists.isLoading && <LoadingPlaceholder />}
      {lists.data?.items.map((list) => <ListRow key={list.id} list={list} />)}
      {lists.data?.items.length === 0 && !draft && (
        <p className="text-slate-500">{t('lists.empty')}</p>
      )}

      {draft ? (
        <div ref={formRef} className="card space-y-2 p-3">
          <input
            className="input"
            placeholder={t('lists.namePlaceholder')}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            maxLength={LIST_NAME_MAX_LENGTH}
            autoFocus
          />
          <textarea
            className="input min-h-[80px]"
            placeholder={t('lists.descriptionPlaceholder')}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            maxLength={LIST_DESCRIPTION_MAX_LENGTH}
          />
          <ErrorMessage message={createError} />
          <div className="flex justify-end gap-2">
            <button
              className="btn-ghost h-9 px-3 text-sm"
              onClick={() => {
                setDraft(null);
                setCreateError(null);
              }}
            >
              {t('lists.cancel')}
            </button>
            <button
              className="btn-primary h-9 px-3 text-sm"
              onClick={submitCreate}
              disabled={create.isPending || !draft.name.trim()}
            >
              {t('lists.create')}
            </button>
          </div>
        </div>
      ) : (
        <button className="btn-secondary h-9 w-full text-sm" onClick={startNew}>
          <Plus className="h-4 w-4" />
          {t('lists.newList')}
        </button>
      )}

      <button
        className="btn-secondary mt-4 w-full"
        onClick={() => navigate('/history')}
      >
        <HistoryIcon className="h-4 w-4" />
        {t('lists.showFullHistory')}
      </button>
    </div>
  );
}

function ListRow({ list }: { list: ListDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdateList();
  const del = useDeleteList();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description ?? '');
  const [error, setError] = useState<string | null>(null);

  const localized = localizeListName(list, t);

  const handleSave = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        id: list.id,
        name: name.trim(),
        description: description.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('lists.deleteConfirm', { name: localized }))) return;
    setError(null);
    try {
      await del.mutateAsync(list.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  if (editing) {
    return (
      <div className="card space-y-2 p-3">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={LIST_NAME_MAX_LENGTH}
          autoFocus
        />
        <textarea
          className="input min-h-[80px]"
          placeholder={t('lists.descriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={LIST_DESCRIPTION_MAX_LENGTH}
        />
        <ErrorMessage message={error} />
        <div className="flex justify-end gap-2">
          <button
            className="btn-ghost h-9 px-3 text-sm"
            onClick={() => {
              setEditing(false);
              setName(list.name);
              setDescription(list.description ?? '');
              setError(null);
            }}
          >
            <X className="h-4 w-4" />
            {t('lists.cancel')}
          </button>
          <button
            className="btn-primary h-9 px-3 text-sm"
            onClick={handleSave}
            disabled={update.isPending || !name.trim()}
          >
            <Check className="h-4 w-4" />
            {t('lists.save')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card flex items-stretch gap-1 p-3">
      <button
        type="button"
        onClick={() => navigate(`/lists/${list.id}`)}
        className="flex min-w-0 flex-1 flex-col items-start text-left"
      >
        <div className="flex w-full items-center gap-2">
          <p className="min-w-0 flex-1 truncate font-medium text-slate-900">{localized}</p>
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
            {list.itemCount}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        </div>
        {list.description && (
          <p className="mt-1 line-clamp-2 text-sm text-slate-500">{list.description}</p>
        )}
      </button>
      {!list.isSystem && (
        <div className="flex flex-col gap-1">
          <button
            className="btn-ghost h-8 px-2"
            onClick={() => setEditing(true)}
            aria-label={t('lists.rename')}
            title={t('lists.rename')}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            className="btn-ghost h-8 px-2 text-rose-600"
            onClick={handleDelete}
            aria-label={t('lists.delete')}
            title={t('lists.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
